import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, getToken, setToken, getCachedUser, setCachedUser } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(() => getCachedUser());
  const [loading, setLoading] = useState(true);

  const setUser = useCallback((next) => {
    setUserState(next);
    setCachedUser(next);
  }, []);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const data = await api('/auth/me');
      if (data.token) setToken(data.token);
      setUser(data.user);
      return data.user;
    } catch (err) {
      // Only force logout on auth failure — keep session through deploys / brief outages
      if (err?.status === 401) {
        setToken('');
        setUser(null);
        return null;
      }
      // Keep cached user if we already have one
      return getCachedUser();
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const data = await api('/auth/login', {
      method: 'POST',
      body: { email, password },
      auth: false,
    });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async ({ name, email, phone, password }) => {
    const data = await api('/auth/register', {
      method: 'POST',
      body: { name, email, phone, password },
      auth: false,
    });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    setToken('');
    setUser(null);
  };

  const updateProfile = async (patch) => {
    const data = await api('/auth/profile', { method: 'PATCH', body: patch });
    setUser(data.user);
    return data.user;
  };

  const skipAddressPrompt = async () => updateProfile({ addressPromptDismissed: true });

  const addAddress = async (address) => {
    const data = await api('/auth/addresses', { method: 'POST', body: address });
    setUser(data.user);
    return data;
  };

  const updateAddress = async (id, address) => {
    const data = await api(`/auth/addresses/${id}`, { method: 'PATCH', body: address });
    setUser(data.user);
    return data;
  };

  const removeAddress = async (id) => {
    const data = await api(`/auth/addresses/${id}`, { method: 'DELETE' });
    setUser(data.user);
    return data.user;
  };

  const setDefaultAddress = async (id) => {
    const data = await api(`/auth/addresses/${id}/default`, { method: 'POST', body: {} });
    setUser(data.user);
    return data.user;
  };

  const addresses = user?.addresses || [];
  const defaultAddress =
    addresses.find((a) => a.id === user?.defaultAddressId) || addresses.find((a) => a.isDefault) || addresses[0] || null;
  const needsAddressSetup =
    Boolean(user) && user.role !== 'admin' && addresses.length === 0 && !user.addressPromptDismissed;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAdmin: user?.role === 'admin',
        isLoggedIn: Boolean(user),
        addresses,
        defaultAddress,
        needsAddressSetup,
        login,
        register,
        logout,
        refresh,
        updateProfile,
        skipAddressPrompt,
        addAddress,
        updateAddress,
        removeAddress,
        setDefaultAddress,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
